/* ═════════════════════════════════════════════════════════════════
   Vinci Compliance Capsule — Forensic Dossier for Razorpay/audit
   ═════════════════════════════════════════════════════════════════ */
import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck, FileText, Loader2, Search, Download, Send, Copy,
  Building2, Award, Briefcase, Landmark, BadgeCheck, Video,
  Sparkles, Milestone, MessageSquare, Zap, Upload, Trash2, Mail,
  Image as ImageIcon, Zap as ZapIcon, ClipboardList, AlertOctagon,
} from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export function AdminComplianceCapsule({ api }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rzp, setRzp] = useState(null);
  const [copied, setCopied] = useState(null);

  const doSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) { setRows([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/compliance/users?q=${encodeURIComponent(query)}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally { setSearching(false); }
  }, [api]);

  useEffect(() => {
    const t = setTimeout(() => doSearch(q), 250);
    return () => clearTimeout(t);
  }, [q, doSearch]);

  const loadDossier = async (userId) => {
    setLoading(true);
    setDossier(null);
    setRzp(null);
    try {
      const { data } = await api.get(`/admin/compliance/dossier/${userId}`);
      setDossier(data);
      setSelected(userId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load dossier");
    } finally { setLoading(false); }
  };

  const loadRazorpay = async () => {
    if (!dossier) return;
    try {
      const { data } = await api.get(
        `/admin/compliance/dossier/${dossier.partner.user_id}/razorpay-response`
      );
      setRzp(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not draft response");
    }
  };

  const copyToClipboard = async (text, tag) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag);
      toast.success(`${tag} copied`);
      setTimeout(() => setCopied(null), 1500);
    } catch { toast.error("Clipboard unavailable"); }
  };

  return (
    <div data-testid="admin-compliance-capsule" className="space-y-6">
      <div>
        <div className="overline text-[#D8B23E] inline-flex items-center gap-2">
          <ShieldCheck size={12} /> Forensic Audit
        </div>
        <h2 className="font-serif text-3xl mt-2">Vinci Compliance Capsule</h2>
        <p className="text-[13px] text-[#8A8A82] font-body mt-1 max-w-3xl">
          Aggregates a single partner's B-KYC, signed agreements, video-witness, 8-milestone
          trade proof and satisfaction pulse into an audit-grade Forensic Dossier — with a
          drop-in Razorpay Risk-Desk response template.
        </p>
      </div>

      {/* Search bar */}
      <div className="relative max-w-2xl">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A6A1A]" />
        <input data-testid="capsule-search-input"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by email or business name…"
                 className="w-full pl-10 pr-4 py-3 bg-black/50 border border-white/15 rounded-sm text-[#F5F5F0] focus:border-[#D8B23E] outline-none font-body" />
        {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#8A6A1A]" />}
      </div>

      {rows.length > 0 && !dossier && (
        <div className="space-y-2 max-w-2xl">
          {rows.map((r) => (
            <button data-testid={`capsule-partner-${r.user_id}`}
                      key={r.user_id}
                      onClick={() => loadDossier(r.user_id)}
                      className="w-full text-left border border-white/10 hover:border-[#D8B23E] bg-[#0A0906] rounded-sm px-4 py-3 flex items-center justify-between transition-colors">
              <div>
                <div className="text-[#F5F5F0] font-body"><b>{r.business_name || r.name || "—"}</b></div>
                <div className="text-[11px] text-[#8A8A82]">{r.email} · {r.vinci_code || "—"}</div>
              </div>
              <span className={`text-[9px] font-grotesk uppercase tracking-[0.22em] px-2 py-0.5 rounded-full
                                ${r.status === "active" ? "bg-[#2FB999]/15 text-[#2FB999] border border-[#2FB999]/40"
                                                            : "bg-[#D8B23E]/15 text-[#D8B23E] border border-[#D8B23E]/40"}`}>
                {r.status || "—"}
              </span>
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="text-[#8A8A82] py-10 text-center"><Loader2 className="inline animate-spin" /></div>
      )}

      <AnimatePresence>
        {dossier && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <DossierView dossier={dossier} api={api} backend={backend}
                          onOpenRazorpay={loadRazorpay}
                          rzp={rzp} copied={copied}
                          onCopy={copyToClipboard}
                          onWitnessChanged={() => loadDossier(selected)}
                          onClose={() => { setDossier(null); setSelected(null); setRzp(null); }} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Dossier view ─────────────────────────────────────────────── */
function DossierView({ dossier, api, backend, onOpenRazorpay, rzp, copied, onCopy, onClose, onWitnessChanged }) {
  const p = dossier.partner;
  const m = dossier.merchant_profile;
  const idv = dossier.identity_verification;
  const t = dossier.trade_proof;
  const secure = dossier.verification_status === "SECURE";
  const pdfHref = `${backend}/api/admin/compliance/dossier/${p.user_id}/pdf`;

  return (
    <div data-testid="capsule-dossier" className="space-y-5">
      {/* Header ribbon */}
      <div className="relative overflow-hidden rounded-sm border border-[#D8B23E]/40 p-5"
             style={{ background: "linear-gradient(120deg,#0A0906 0%,#161009 60%,#0A0906 100%)" }}>
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
               style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #D8B23E 0%, transparent 40%), radial-gradient(circle at 80% 60%, #B87333 0%, transparent 40%)" }} />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <VinciShield />
            <div>
              <div className="overline text-[#D8B23E] flex items-center gap-2">
                <BadgeCheck size={11} /> Official Regulatory Compliance Dossier
              </div>
              <h3 className="font-serif text-2xl mt-1" style={{ color: "#F6E4A6" }}>{dossier.srn}</h3>
              <p className="text-[11px] text-[#8A8A82] font-body">
                Generated {new Date(dossier.generated_at).toLocaleString()} · Vinci Executive Command · Mumbai
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div data-testid="capsule-verification-badge"
                   className={`px-4 py-2 rounded-sm font-grotesk text-[10px] uppercase tracking-[0.22em] flex items-center gap-2
                                ${secure ? "bg-[#2FB999] text-black" : "bg-[#C0392B] text-white"}`}>
              <ShieldCheck size={12} /> Verification Status: {dossier.verification_status}
            </div>
            <a href={pdfHref} target="_blank" rel="noreferrer"
                 data-testid="capsule-download-pdf"
                 className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] px-4 py-2 rounded-sm inline-flex items-center gap-1.5">
              <Download size={12} /> Generate Compliance Dossier PDF
            </a>
            <button onClick={onOpenRazorpay}
                      data-testid="capsule-razorpay-btn"
                      className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-white border border-[#D8B23E] bg-black/50 hover:bg-[#D8B23E]/10 px-4 py-2 rounded-sm inline-flex items-center gap-1.5">
              <Send size={12} /> Draft Razorpay Response
            </button>
            <button onClick={onClose}
                      className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#8A8A82] px-3 py-2">
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Section grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section num="1" title="Merchant Profile" icon={<Building2 size={12} />}>
          <div className="grid grid-cols-2 gap-3">
            <KV label="Business" value={m.business_name} />
            <KV label="Vinci Code" value={p.vinci_code} mono />
            <KV label="GSTIN" value={m.gstin} mono />
            <KV label="PAN" value={m.pan} mono />
            <KV label="Udyam Aadhaar" value={m.udyam} mono />
            <KV label="CIN" value={m.cin} mono />
            <KV label="Registered State" value={p.state} />
            <KV label="Approved On" value={p.approved_at?.slice(0, 10)} />
          </div>
          <div className="mt-3">
            <KV label="Registered Address" value={m.registered_address} />
          </div>
        </Section>

        <Section num="3" title="Identity Verification" icon={<BadgeCheck size={12} />}>
          <div className="grid grid-cols-2 gap-3">
            <KV label="eKYC Provider" value={idv.ekyc_provider || "In-house"} />
            <KV label="KYC Status" value={idv.kyc_status} />
            <KV label="Zoho Sign Doc ID" value={idv.zoho_sign_document_id} mono />
            <KV label="Video Witness" value={idv.video_witness_url ? "Captured" : "Not captured"} />
          </div>
          {(idv.ekyc_checks || []).length > 0 && (
            <div className="mt-3">
              <div className="overline text-[#8A6A1A] mb-1.5">eKYC Checks</div>
              <div className="grid grid-cols-2 gap-1.5">
                {idv.ekyc_checks.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-[12px] font-body">
                    <span className={`h-2 w-2 rounded-full ${c.ok ? "bg-[#2FB999]" : "bg-[#C0392B]"}`} />
                    <span className="text-[#C9C9C2]">{c.label}</span>
                    <span className={c.ok ? "text-[#2FB999]" : "text-[#C0392B]"}>· {c.ok ? "PASS" : "FAIL"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(idv.signed_documents || []).length > 0 && (
            <div className="mt-3">
              <div className="overline text-[#8A6A1A] mb-1.5">Signed Agreements</div>
              <ul className="space-y-1 text-[12px] text-[#C9C9C2] font-body">
                {idv.signed_documents.map((s, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <FileText size={11} className="text-[#D8B23E]" />
                    <b>{s.title}</b>
                    <span className="text-[#8A8A82]">· {(s.signed_at || "—").slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <VideoWitnessPanel
            api={api}
            userId={p.user_id}
            idv={idv}
            onChanged={onWitnessChanged}
          />
        </Section>
      </div>

      {/* Trade Proof — full-width */}
      <Section num="2" title="Trade Proof · 8-Milestone Workflow" icon={<Milestone size={12} />}>
        <div className="grid grid-cols-3 gap-3 mb-4 rounded-sm p-3"
               style={{ background: "linear-gradient(90deg,#0F0C05 0%,#221808 100%)" }}>
          <StatTile label="Orders" value={t.orders_count} />
          <StatTile label="Total Value" value={inr(t.total_value_inr)} accent />
          <StatTile label="Paid · Captured" value={inr(t.total_paid_inr)} />
        </div>
        {(t.orders || []).length === 0 ? (
          <div className="text-[#8A8A82] font-body italic text-[13px]">No orders recorded for this partner.</div>
        ) : (
          <div className="space-y-3">
            {t.orders.slice(0, 8).map((o) => (
              <div key={o.id} className="border border-white/10 rounded-sm p-3 bg-black/30">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[13px] text-[#D8B23E]">{o.po_number}</span>
                    <span className="text-[11px] text-[#8A8A82]">· {(o.created_at || "").slice(0, 10)}</span>
                    <span className="text-[11px] text-[#F6E4A6] font-mono">· {inr(o.total)}</span>
                    <span className="text-[10px] text-[#8A8A82]">pay: <b className="text-[#F5F5F0]">{o.payment_status || "—"}</b></span>
                    <span className="text-[10px] text-[#8A8A82]">fulfil: <b className="text-[#F5F5F0]">{o.fulfillment_status || "—"}</b></span>
                  </div>
                </div>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                  {o.milestones.map((ms, i) => (
                    <div key={i} className="text-center">
                      <div className={`h-6 w-6 mx-auto rounded-full flex items-center justify-center border
                                          ${ms.done ? "bg-[#2FB999] border-[#2FB999] text-black"
                                                     : "bg-transparent border-white/20 text-[#5A5A55]"}`}>
                        {ms.done ? "✓" : i + 1}
                      </div>
                      <div className="mt-1 text-[9px] font-grotesk uppercase tracking-[0.14em] text-[#8A8A82] leading-tight">
                        {ms.stage}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Pulse */}
      <Section num="4" title="Satisfaction Pulse" icon={<MessageSquare size={12} />}>
        {(dossier.satisfaction_pulse || []).length === 0 ? (
          <div className="text-[#8A8A82] font-body italic text-[13px]">No pulse-feedback rows on file.</div>
        ) : (
          <div className="space-y-3">
            {dossier.satisfaction_pulse.map((r, i) => (
              <div key={i} className="border-l-2 border-[#D8B23E] pl-3">
                <div className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#8A6A1A]">
                  {r.source} · {(r.at || "").slice(0, 10)}
                  {r.score !== null && r.score !== undefined && <> · Score <b className="text-[#F6E4A6]">{r.score}</b></>}
                </div>
                <div className="text-[13px] text-[#F5F5F0] font-body mt-1 italic">"{r.remark}"</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* System Audit Certificate */}
      <div className="relative rounded-sm border border-[#D8B23E]/50 p-5"
             style={{ background: "linear-gradient(90deg,#0F0C05 0%,#231A08 50%,#0F0C05 100%)" }}>
        <div className="flex items-center gap-3 mb-3">
          <Award className="text-[#D8B23E]" size={22} />
          <div>
            <div className="overline text-[#D8B23E]">System Audit Certificate</div>
            <div className="font-serif text-lg text-[#F6E4A6]">
              Every trade verified by Vinci Executive Command
            </div>
          </div>
        </div>
        <p className="text-[12px] text-[#C9C9C2] font-body">
          Certified — every transaction in this dossier is verified by the Vinci Executive Command with multi-layer eKYC, Zoho Sign MSA and immutable-ledger stamping. All records are archived to Emergent Object Storage and are retrievable through Vinci's Legal Desk within 24 hours of a written audit request.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          {["NSDL", "GeM", "MSME", "STARTUP INDIA", "ISO 9001", "IT-Act §5"].map((tag) => (
            <span key={tag} className="text-[9px] font-grotesk uppercase tracking-[0.22em] border border-[#D8B23E]/40 text-[#D8B23E] px-2.5 py-1 rounded-full">
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Razorpay response drawer */}
      <AnimatePresence>
        {rzp && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        data-testid="capsule-razorpay-response"
                        className="rounded-sm border border-[#D8B23E]/50 p-5 bg-[#0A0906]">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div>
                <div className="overline text-[#D8B23E] flex items-center gap-2">
                  <Zap size={12} /> Razorpay Risk-Desk Response Template
                </div>
                <div className="font-serif text-lg mt-1">{rzp.subject}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button data-testid="copy-rzp-subject"
                          onClick={() => onCopy(rzp.subject, "Subject")}
                          className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-white/20 text-[#C9C9C2] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                  <Copy size={11} /> {copied === "Subject" ? "Copied" : "Copy Subject"}
                </button>
                <button data-testid="copy-rzp-plain"
                          onClick={() => onCopy(rzp.plain, "Plain-text")}
                          className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-white/20 text-[#C9C9C2] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                  <Copy size={11} /> {copied === "Plain-text" ? "Copied" : "Copy Plain-Text"}
                </button>
                <button data-testid="copy-rzp-html"
                          onClick={() => onCopy(rzp.html, "HTML")}
                          className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                  <Copy size={11} /> {copied === "HTML" ? "Copied" : "Copy Rich HTML"}
                </button>
              </div>
            </div>
            <div className="bg-black/40 border border-white/10 rounded-sm p-4 max-h-[420px] overflow-y-auto text-[13px] text-[#E8E8DC] leading-relaxed"
                   dangerouslySetInnerHTML={{ __html: rzp.html }} />
            <div className="mt-3 text-[11px] text-[#8A8A82] font-body italic">
              Recommended: attach the Compliance Dossier PDF and reply from <b>trade@realtymaze.com</b> to Razorpay's Risk Desk in one thread.
            </div>
            <SendLiveResponsePanel api={api} dossier={dossier} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Always-visible Razorpay Risk-Guard panels */}
      <RequestPulsePanel api={api} dossier={dossier} />
      <CoverLetterPanel api={api} dossier={dossier} onCopy={onCopy} copied={copied} />
    </div>
  );
}

/* ── Request Razorpay Proof (Pulse) ─────────────────────────── */
function RequestPulsePanel({ api, dossier }) {
  const [project, setProject] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [pulses, setPulses] = useState([]);

  const loadPulses = async () => {
    try {
      const { data } = await api.get(
        `/admin/compliance/dossier/${dossier.partner.user_id}/pulses`);
      setPulses(data.rows || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadPulses(); }, []);  // eslint-disable-line

  const request = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/compliance/dossier/${dossier.partner.user_id}/request-pulse`,
        { project_name: project });
      setResult(data);
      toast.success(data.email_delivered
                       ? `Pulse email sent to ${dossier.partner.email}`
                       : "Pulse created — email may be queued");
      await loadPulses();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="request-pulse-panel"
           className="mt-6 rounded-sm border border-[#D8B23E]/40 p-5 bg-[#0A0906]">
      <div className="overline text-[#D8B23E] flex items-center gap-2 mb-3">
        <ClipboardList size={12} /> Razorpay Risk-Guard · Request Trade Pulse
      </div>
      <p className="text-[12px] text-[#C9C9C2] font-body mb-3">
        Sends the partner an authoritative "Executive Mandate · Trade Pulse Verification" email with a signed one-time link. Their submission auto-emails you a Razorpay-visible thread with a Permanent Declaration Video (optional).
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <input data-testid="pulse-project-name"
                 value={project} onChange={(e) => setProject(e.target.value)}
                 placeholder="Project name (e.g. Mumbai Metro Steel Link)"
                 className="flex-1 min-w-[240px] bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] text-sm" />
        <button data-testid="request-pulse-btn"
                  onClick={request} disabled={busy}
                  className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] px-5 py-2 rounded-sm inline-flex items-center gap-1.5">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
          Request Razorpay Proof
        </button>
      </div>
      {result?.link && (
        <div data-testid="pulse-request-result"
               className="mt-3 text-[12px] font-body bg-[#0F0C05] border border-[#2FB999]/40 text-[#2FB999] px-3 py-2 rounded-sm">
          <div>Sent to <b>{dossier.partner.email}</b> · expires {new Date(result.expires_at).toLocaleDateString()}.</div>
          <div className="text-[10px] text-[#8A8A82] mt-1 break-all">Link: {result.link}</div>
          <div className="text-[10px] text-[#8A8A82]">Audit password (share with Razorpay): <b className="font-mono text-[#F6E4A6]">{result.audit_password}</b></div>
        </div>
      )}
      {pulses.length > 0 && (
        <div className="mt-4 border-t border-white/10 pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="overline text-[#8A6A1A]">Pulse History</div>
            {pulses.some(p => p.video_key) && (
              <a
                data-testid="pulse-bulk-videos-zip"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/compliance/dossier/${dossier.partner.user_id}/videos.zip`}
                download
                className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.28em] text-[#F6E4A6] border border-[#D8B23E]/60 hover:bg-[#D8B23E]/10 px-2.5 py-1 rounded-sm"
                title="Zip & download every declaration video on file for this partner"
              >
                ⬇ Download all videos (.zip)
              </a>
            )}
          </div>
          <div className="space-y-2">
            {pulses.map((p) => (
              <div key={p.token} className="text-[11px] text-[#C9C9C2] font-body">
                <span className="font-mono text-[#D8B23E]">VNC-CAP-PULSE-{p.token.slice(0, 8).toUpperCase()}</span>
                {" · "}<b>{p.status}</b>
                {" · "}requested {new Date(p.requested_at).toLocaleString()}
                {p.form?.satisfied !== undefined && (
                  <span className={p.form.satisfied ? " text-[#2FB999]" : " text-[#C0392B]"}>
                    {" · "}{p.form.satisfied ? "✅ Satisfied" : "❌ Not satisfied"}
                  </span>
                )}
                {p.video_key && (
                  <a
                    data-testid={`pulse-video-download-${p.token.slice(0, 8)}`}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.video_key}?download=1`}
                    download
                    className="ml-1 inline-flex items-center gap-1 text-[#F6E4A6] hover:text-[#FFF3C5] underline decoration-[#D8B23E]/40 underline-offset-2"
                    title="Download declaration video"
                  >
                    🎥 Download video
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Contextual Razorpay Cover Letter ─────────────────────────── */
function CoverLetterPanel({ api, dossier, onCopy, copied }) {
  const [query, setQuery] = useState("");
  const [project, setProject] = useState("");
  const [busy, setBusy] = useState(false);
  const [letter, setLetter] = useState(null);

  const draft = async () => {
    if ((query || "").trim().length < 20) {
      toast.error("Paste the Razorpay flag email (min 20 chars)"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/compliance/dossier/${dossier.partner.user_id}/razorpay-cover-letter`,
        { query_text: query, project_name: project });
      setLetter(data);
      toast.success("Cover letter drafted with query-specific context");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="cover-letter-panel"
           className="mt-6 rounded-sm border border-[#D8B23E]/40 p-5 bg-[#0A0906]">
      <div className="overline text-[#D8B23E] flex items-center gap-2 mb-3">
        <AlertOctagon size={12} /> Query-Specific Cover Letter · Razorpay Risk Vertical
      </div>
      <p className="text-[12px] text-[#C9C9C2] font-body mb-3">
        Paste Razorpay's flag email verbatim. Vinci parses amounts, dates, payment/order IDs and ticket refs, and drafts a data-specific reply that addresses each point.
      </p>
      <textarea data-testid="cover-letter-query"
                  value={query} onChange={(e) => setQuery(e.target.value)}
                  rows={6} placeholder="Paste the full Razorpay Risk email here…"
                  className="w-full bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] text-sm font-mono" />
      <div className="mt-2 flex flex-wrap gap-2 items-center">
        <input data-testid="cover-letter-project"
                 value={project} onChange={(e) => setProject(e.target.value)}
                 placeholder="Project on record (optional)"
                 className="flex-1 min-w-[240px] bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] text-sm" />
        <button data-testid="cover-letter-draft"
                  onClick={draft} disabled={busy}
                  className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] px-5 py-2 rounded-sm inline-flex items-center gap-1.5">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
          Draft Query-Specific Letter
        </button>
      </div>
      {letter && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-serif text-lg">{letter.subject}</div>
            <div className="flex gap-2">
              <button onClick={() => onCopy(letter.subject, "CL-Subject")}
                        className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-white/20 text-[#C9C9C2] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                <Copy size={11} /> {copied === "CL-Subject" ? "Copied" : "Copy Subject"}
              </button>
              <button onClick={() => onCopy(letter.plain, "CL-Plain")}
                        className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-white/20 text-[#C9C9C2] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                <Copy size={11} /> {copied === "CL-Plain" ? "Copied" : "Copy Plain"}
              </button>
              <button onClick={() => onCopy(letter.html, "CL-HTML")}
                        className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                <Copy size={11} /> {copied === "CL-HTML" ? "Copied" : "Copy Rich HTML"}
              </button>
            </div>
          </div>
          <div className="bg-black/40 border border-white/10 rounded-sm p-4 max-h-[420px] overflow-y-auto text-[13px] text-[#E8E8DC] leading-relaxed"
                 dangerouslySetInnerHTML={{ __html: letter.html }} />
          {letter.context && (
            <div className="text-[11px] text-[#8A8A82] font-body">
              Extracted: {letter.context.amounts?.length || 0} amount(s) ·
              {" "}{letter.context.dates?.length || 0} date(s) ·
              {" "}{letter.context.payment_ids?.length || 0} payment id(s) ·
              {" "}{letter.context.order_ids?.length || 0} order id(s) ·
              {" "}{letter.context.ticket_refs?.length || 0} ticket ref(s)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Send Live Response (transmit) ────────────────────────────── */
function SendLiveResponsePanel({ api, dossier }) {
  const [to, setTo] = useState("risk@razorpay.com");
  const [cc, setCc] = useState("trade@realtymaze.com");
  const [note, setNote] = useState("");
  const [screenshots, setScreenshots] = useState([]);
  const [includeLedgerCsv, setIncludeLedgerCsv] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const shotRef = useRef(null);

  const addImages = (files) => {
    const next = [...screenshots];
    for (const f of files || []) {
      if (!f.type?.startsWith("image/")) continue;
      if (f.size > 6 * 1024 * 1024) { toast.error(`${f.name} > 6 MB — skipped`); continue; }
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = (reader.result || "").toString().split(",")[1];
        next.push({ filename: f.name || `screenshot-${next.length + 1}.png`,
                     content_type: f.type || "image/png",
                     content: b64,
                     preview: reader.result });
        setScreenshots([...next]);
      };
      reader.readAsDataURL(f);
    }
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    const files = [];
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) { e.preventDefault(); addImages(files); }
  };

  const send = async () => {
    if (!to.includes("@")) { toast.error("Valid recipient required"); return; }
    setBusy(true);
    try {
      const ccList = cc.split(",").map((x) => x.trim()).filter((x) => x.includes("@"));
      const { data } = await api.post(
        `/admin/compliance/dossier/${dossier.partner.user_id}/transmit-razorpay`,
        { to_email: to, cc: ccList, note,
          screenshots: screenshots.map(({ filename, content_type, content }) =>
            ({ filename, content_type, content })),
          include_ledger_csv: includeLedgerCsv }
      );
      setResult(data);
      if (data.ok) {
        toast.success(`Dossier sent to ${data.delivered_to.join(", ")}`);
      } else {
        toast.error(`Delivery failed — ${(data.failed || []).map((f) => f.email).join(", ")}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="send-live-response-panel" onPaste={onPaste}
           className="mt-4 border-t border-[#D8B23E]/30 pt-4">
      <div className="overline text-[#D8B23E] flex items-center gap-2 mb-3">
        <Mail size={12} /> Send Live Response
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A] font-grotesk">
            To (Razorpay Risk Desk)
          </label>
          <input data-testid="transmit-to"
                   type="email" value={to} onChange={(e) => setTo(e.target.value)}
                   className="w-full mt-1 bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A] font-grotesk">
            CC (comma-separated)
          </label>
          <input data-testid="transmit-cc"
                   value={cc} onChange={(e) => setCc(e.target.value)}
                   placeholder="trade@realtymaze.com, legal@realtymaze.com"
                   className="w-full mt-1 bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] text-sm" />
        </div>
      </div>
      <div className="mt-3">
        <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A] font-grotesk">
          Personal note (optional — appears in the letter body)
        </label>
        <textarea data-testid="transmit-note"
                    rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Please refer to Section 2 for physical dispatch proof."
                    className="w-full mt-1 bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] text-sm" />
      </div>

      {/* Screenshot proof zone */}
      <div className="mt-3">
        <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A] font-grotesk">
          Screenshot Proof · Paste (⌘V / Ctrl-V) or Upload
        </label>
        <div className="mt-1 border border-dashed border-[#D8B23E]/50 rounded-sm p-3 bg-black/30"
               data-testid="screenshot-drop-zone">
          <div className="flex flex-wrap gap-3">
            {screenshots.map((s, i) => (
              <div key={i} className="relative group">
                <img src={s.preview} alt={s.filename}
                       className="h-24 w-24 object-cover rounded-sm border border-[#D8B23E]/50" />
                <button onClick={() => setScreenshots(screenshots.filter((_, x) => x !== i))}
                          data-testid={`screenshot-remove-${i}`}
                          className="absolute -top-2 -right-2 bg-[#C0392B] text-white h-5 w-5 rounded-full flex items-center justify-center text-[11px] leading-none opacity-0 group-hover:opacity-100 transition-opacity">
                  ×
                </button>
                <div className="text-[9px] text-[#8A8A82] mt-1 truncate max-w-24">{s.filename}</div>
              </div>
            ))}
            <button onClick={() => shotRef.current?.click()}
                      data-testid="screenshot-add"
                      className="h-24 w-24 border-2 border-dashed border-[#D8B23E]/50 hover:border-[#D8B23E] rounded-sm flex flex-col items-center justify-center gap-1 text-[#D8B23E] hover:bg-[#D8B23E]/10">
              <ImageIcon size={16} />
              <span className="text-[9px] font-grotesk uppercase tracking-[0.18em]">Add</span>
            </button>
          </div>
          <input ref={shotRef} type="file" accept="image/*" multiple
                   onChange={(e) => { addImages(e.target.files); e.target.value = ""; }}
                   data-testid="screenshot-input"
                   className="hidden" />
          <p className="text-[10px] text-[#8A8A82] font-body italic mt-2">
            Paste a Razorpay-console screenshot anywhere in this panel — it's auto-attached and embedded inline in the letter.
          </p>
        </div>
      </div>

      <label className="mt-3 flex items-center gap-2 text-[11px] text-[#C9C9C2] font-body cursor-pointer">
        <input type="checkbox" checked={includeLedgerCsv}
                 data-testid="ledger-csv-toggle"
                 onChange={(e) => setIncludeLedgerCsv(e.target.checked)}
                 className="accent-[#D8B23E]" />
        Auto-attach immutable-ledger CSV (SRN × PO × Milestones)
      </label>

      <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
        <p className="text-[11px] text-[#8A8A82] font-body italic">
          Sends the Rich HTML letter + Forensic Dossier PDF + optional CSV + screenshot(s) in one email.
        </p>
        <button data-testid="transmit-send"
                  onClick={send} disabled={busy}
                  className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#2FB999] to-[#5FD3B7] px-5 py-2 rounded-sm inline-flex items-center gap-1.5">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
          Send Live Response
        </button>
      </div>
      {result && (
        <div data-testid="transmit-result"
               className={`mt-3 text-[12px] font-body px-3 py-2 rounded-sm
                            ${result.ok ? "bg-[#2FB999]/10 text-[#2FB999] border border-[#2FB999]/40"
                                          : "bg-[#C0392B]/10 text-[#C0392B] border border-[#C0392B]/40"}`}>
          {result.ok
            ? <>Delivered to <b>{result.delivered_to.join(", ")}</b> at {new Date(result.at).toLocaleString()}
                 · attachments: {(result.attachments || []).join(", ")}</>
            : <>Failed: {(result.failed || []).map((f) => `${f.email} · ${f.error}`).join(" · ")}</>}
        </div>
      )}
    </div>
  );
}

/* ── Video Witness upload + embed ─────────────────────────────── */
function VideoWitnessPanel({ api, userId, idv, onChanged }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 60 * 1024 * 1024) {
      toast.error("Video must be under 60 MB"); return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post(
        `/admin/compliance/dossier/${userId}/video-witness`,
        form, { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Video witness stored");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const clear = async () => {
    if (!window.confirm("Delete the stored video witness for this partner?")) return;
    try {
      await api.delete(`/admin/compliance/dossier/${userId}/video-witness`);
      toast.success("Video witness cleared");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const streamUrl = idv.video_witness_embedded
    ? `${backend}/api/admin/compliance/dossier/${userId}/video-witness/view`
    : (idv.video_witness_url && idv.video_witness_url.startsWith("http") ? idv.video_witness_url : null);

  return (
    <div className="mt-3">
      <div className="overline text-[#8A6A1A] mb-1.5 flex items-center gap-2">
        <Video size={11} /> Video Witness
        {idv.video_witness_embedded && (
          <span className="text-[9px] text-[#2FB999] font-grotesk uppercase tracking-[0.22em]">
            · vault-embedded
          </span>
        )}
      </div>
      {streamUrl ? (
        <video data-testid="video-witness-embed"
                 src={streamUrl}
                 controls preload="metadata"
                 className="w-full max-h-56 rounded-sm border border-white/10 bg-black" />
      ) : (
        <div className="text-[12px] text-[#8A8A82] font-body italic">
          No video witness on file yet.
        </div>
      )}
      <div className="mt-2 flex items-center gap-2">
        <input ref={fileRef} type="file" accept="video/*" onChange={upload}
                 data-testid="video-witness-input"
                 className="hidden" />
        <button data-testid="video-witness-upload"
                  onClick={() => fileRef.current?.click()}
                  disabled={busy}
                  className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-[#D8B23E] text-[#D8B23E] hover:bg-[#D8B23E]/10 px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
          {idv.video_witness_embedded ? "Replace" : "Upload"} Witness
        </button>
        {idv.video_witness_embedded && (
          <button data-testid="video-witness-delete"
                    onClick={clear}
                    className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-[#C0392B]/50 text-[#C0392B] hover:bg-[#C0392B]/10 px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
            <Trash2 size={11} /> Clear
          </button>
        )}
        {idv.video_witness_uploaded_at && (
          <span className="text-[10px] text-[#8A8A82] font-body italic">
            Uploaded {new Date(idv.video_witness_uploaded_at).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Building blocks ────────────────────────────────────────────── */
function Section({ num, title, icon, children }) {
  return (
    <div className="border border-white/10 rounded-sm p-4 bg-[#0A0906]">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#D8B23E]">§{num}</span>
        <div className="flex items-center gap-1.5 text-[#8A6A1A]">{icon}</div>
        <h4 className="font-serif text-sm text-[#F6E4A6] uppercase tracking-[0.18em]">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div>
      <div className="overline text-[#8A6A1A]">{label}</div>
      <div className={`text-[13px] text-[#F5F5F0] font-body mt-0.5 ${mono ? "font-mono" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div>
      <div className="overline text-[#8A6A1A]">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${accent ? "text-[#F6E4A6]" : "text-[#F5F5F0]"}`}>{value}</div>
    </div>
  );
}

function VinciShield() {
  return (
    <div className="relative flex items-center justify-center">
      <svg width="52" height="52" viewBox="0 0 52 52" className="drop-shadow">
        <defs>
          <linearGradient id="vs-g" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#F6E4A6" />
            <stop offset="0.5" stopColor="#D8B23E" />
            <stop offset="1" stopColor="#B87333" />
          </linearGradient>
        </defs>
        <circle cx="26" cy="26" r="24" stroke="url(#vs-g)" strokeWidth="1.4" fill="none" />
        <circle cx="26" cy="26" r="17" stroke="url(#vs-g)" strokeWidth="0.5" fill="none" opacity="0.6" />
        <text x="26" y="34" textAnchor="middle" fontSize="24" fontFamily="Times, serif" fontWeight="bold" fill="url(#vs-g)">V</text>
      </svg>
    </div>
  );
}
